'use client';
import { Editor } from '@tinymce/tinymce-react';
import Modal from 'components/ui/Modal/modal';
import PrimaryButton from 'components/ui/PrimaryButton';
import SecondaryButton from 'components/ui/SecondaryButton';
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useListGalleryByFolderQuery } from 'redux/reducers/gallery';
import { backendURL } from 'redux/reducers/settings';
import { useAddImage } from 'service/admin.query';
import { handleImageCompress } from 'utils/image-compress';

type TEditor = {
  editData?: any;
  newData: (content: string) => void;
  folderName: string;
  heigth?: number;
  title: string
};


interface ITinyMCE {
  value: string
  setValue: (val: any) => void
  editData?: any;
  newData?: (content: string) => void;
  folderName: string;
  heigth?: number;
  title: string
  id?: string | number
}

const GALLERY_PAGE_SIZE = 20;

export default function TinyMCEEditor({ folderName, title, heigth, value, setValue, id }: ITinyMCE) {
  const editorRef = useRef<any>(null);
  const { mutate: createUploadImage } = useAddImage();

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedGalleryImages, setSelectedGalleryImages] = useState<string[]>([]);
  const [galleryPage, setGalleryPage] = useState(1);

  const { data: galleryList, isLoading: isLoadingGallery, isFetching: isFetchingGallery } = useListGalleryByFolderQuery(
    {
      folderName: 'report',
      page: galleryPage,
      pageSize: GALLERY_PAGE_SIZE,
      routineId: id ?? undefined
    },
    { skip: !galleryOpen || !id }
  );

  const galleryImages = galleryList?.data?.images ?? [];
  const galleryTotal: number = (galleryImages?.[0] as any)?.row_total ?? 0;
  const galleryTotalPages = Math.ceil(galleryTotal / GALLERY_PAGE_SIZE);
  const canPreviousGallery = galleryPage > 1;
  const canNextGallery = galleryPage < galleryTotalPages;

  const buildImageUrl = (path: string) => {
    const baseUrl = backendURL.endsWith('/') ? backendURL : `${backendURL}/`;
    return path.startsWith('http') ? path : `${baseUrl}${path.replace(/^\//, '')}`;
  };

  const toggleGalleryImage = (path: string) => {
    setSelectedGalleryImages((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
    );
  };

  const closeGallery = () => {
    setGalleryOpen(false);
    setSelectedGalleryImages([]);
    setGalleryPage(1);
  };

  const handleInsertGalleryImages = () => {
    if (!selectedGalleryImages.length) {
      toast.warn('Please select at least one image');
      return;
    }
    const html = selectedGalleryImages
      .map((path) => `<img src="${buildImageUrl(path)}" style="max-width:100%;" />`)
      .join('');
    editorRef.current?.insertContent(html);
    closeGallery();
  };

  const uploadHandler = (blobInfo: any): Promise<string> => {
    // Return the promise immediately
    return new Promise(async (resolve, reject) => {
      try {
        const compressFile = await handleImageCompress(blobInfo.blob());

        const payload = {
          file: compressFile,
          folder_name: folderName,
          routine_id: id,
        };

        createUploadImage(payload, {
          onSuccess: (res: any) => {
            if (res?.data?.code === 200) {
              const filePath = res?.data?.data?.path || res?.data?.path;
              const baseUrl = backendURL.endsWith('/') ? backendURL : `${backendURL}/`;
              const fullImageUrl = filePath.startsWith('http')
                ? filePath
                : `${baseUrl}${filePath.replace(/^\//, '')}`;

              resolve(fullImageUrl);
            } else {
              toast.error(res?.data?.message || 'Upload failed');
              reject({ message: 'Upload failed', remove: true });
            }
          },
          onError: (error: any) => {
            toast.error(error?.error?.data?.message || 'Upload failed');
            reject({ message: 'Upload failed', remove: true });
          },
        });
      } catch (error) {
        console.error(error);
        reject("Compression failed");
      }
    });
  };


  const [initialValue, setInitialValue] = useState<string>('')

  return (
    <div className='my-2'>
      <h4>{title ?? ''}</h4>

      <Modal
        title="Select Images from Gallery"
        isModalOpen={galleryOpen}
        closeModal={closeGallery}
        modalSize="modal-xl"
        renderFooter={() => (
          <div className="hstack gap-2 justify-content-end">
            <SecondaryButton onClick={closeGallery} title="Cancel" />
            <PrimaryButton
              type="button"
              title={`Insert ${selectedGalleryImages.length ? `(${selectedGalleryImages.length})` : ''}`}
              onClick={handleInsertGalleryImages}
            />
          </div>
        )}
      >
        <div className="row">
          {(isLoadingGallery || isFetchingGallery) && <p>Loading images...</p>}
          {!isLoadingGallery && !isFetchingGallery && !galleryImages.length && (
            <p>No images found for this routine</p>
          )}
          {galleryImages.map((imgItem: any, index: number) => {
            const imageUrl = buildImageUrl(imgItem.path);
            const isChecked = selectedGalleryImages.includes(imgItem.path);
            return (
              <div key={index} className="col-md-3 mb-4">
                <div
                  className={`position-relative border rounded p-2 ${isChecked ? 'border-primary border-2' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleGalleryImage(imgItem.path)}
                >
                  <input
                    type="checkbox"
                    className="position-absolute form-check-input"
                    style={{
                      top: '8px',
                      left: '8px',
                      width: '18px',
                      height: '18px',
                      zIndex: 10,
                      cursor: 'pointer'
                    }}
                    checked={isChecked}
                    onChange={() => toggleGalleryImage(imgItem.path)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <img
                    src={imageUrl}
                    alt="gallery"
                    className="img-fluid rounded"
                    style={{ height: '160px', objectFit: 'cover', width: '100%' }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {galleryTotalPages > 1 && (
          <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={!canPreviousGallery || isFetchingGallery}
              onClick={() => setGalleryPage((p) => p - 1)}
            >
              Previous
            </button>
            <span className="fs-14">
              Page {galleryPage} of {galleryTotalPages}
            </span>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={!canNextGallery || isFetchingGallery}
              onClick={() => setGalleryPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </Modal>

      <Editor
        tinymceScriptSrc={"/tinymce/tinymce.min.js"}

        onInit={(evt, editor) => {
          editorRef.current = editor;
          setInitialValue(value)
        }}
        initialValue={initialValue}
        value={value}


        init={{
          height: heigth ?? 500,
          menubar: true,
          plugins: [
            'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
            'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
            'insertdatetime', 'media', 'table', 'help', 'wordcount'
          ],
          toolbar: 'undo redo | blocks | ' +
            'bold italic backcolor | alignleft aligncenter ' +
            'alignright alignjustify | bullist numlist outdent indent | ' +
            'removeformat | image gallery code',

          setup: (editor: any) => {
            editor.ui.registry.addButton('gallery', {
              icon: 'image',
              tooltip: 'Insert image from gallery',
              onAction: () => setGalleryOpen(true)
            });
          },

          content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',

          // Image upload settings
          image_advtab: true,
          automatic_uploads: true,
          file_picker_types: 'image',
          images_upload_handler: uploadHandler, // Assigning our handler

          // Sanitize or allow elements
          valid_elements: '*[*]',
          extended_valid_elements: "*[*]",
        }}


        onEditorChange={(content) => {
          setValue(content)
        }}
      />
    </div>
  );
}
